import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import Globe from "@/components/Globe";
import SearchBar from "@/components/SearchBar";
import { useLang } from "@/i18n/LanguageContext";
import { scrollToId } from "@/App";
import { ALL_COUNTRIES, ALL_CITIES } from "@/data/destinations";

const lineAnim = (i) => ({
  initial: { y: "115%" },
  animate: { y: "0%" },
  transition: { duration: 1.1, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
});

export default function Hero() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const globeY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const globeOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "38%"]);

  return (
    <section ref={ref} className="relative flex min-h-screen items-center overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_40%,#0e1838_0%,#050814_60%)]" />

      <motion.div style={{ y: globeY, opacity: globeOpacity }} className="absolute inset-y-0 right-[-32%] w-[110vw] sm:right-[-18%] sm:w-[80vw] lg:right-[-8%] lg:w-[58vw]">
        <Globe className="h-full w-full" />
      </motion.div>

      <motion.div style={{ y: textY }} className="relative z-10 mx-auto w-full max-w-[1500px] px-6 pb-28 pt-36 lg:px-12">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.1 }}
          className="eyebrow mb-8"
          data-testid="hero-eyebrow"
        >
          {t("hero.eyebrow")}
        </motion.p>

        <h1 className="font-serif text-[13vw] font-light leading-[0.98] tracking-tight text-white sm:text-7xl lg:text-[6.2rem]" data-testid="hero-title">
          <span className="mask-line">
            <motion.span {...lineAnim(0)}>{t("hero.title1")}</motion.span>
          </span>
          <span className="mask-line">
            <motion.span {...lineAnim(1)} className="italic text-[#d4af37]">
              {t("hero.title2")}
            </motion.span>
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.75 }}
          className="mt-8 max-w-md text-base font-light leading-relaxed text-white/65 md:text-lg"
          data-testid="hero-sub"
        >
          {t("hero.sub")}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9 }}
          className="mt-10 flex flex-col gap-5"
        >
          <SearchBar />
          <div className="flex flex-wrap items-center gap-4">
            <button
              data-testid="hero-cta"
              onClick={() => scrollToId("destinations")}
              className="btn-gold group flex items-center gap-3 px-8 py-4 text-sm font-semibold tracking-wide"
            >
              {t("hero.cta")}
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-cta-guide"
              onClick={() => scrollToId("alpha-guide")}
              className="btn-outline-gold px-8 py-4 text-sm font-medium tracking-wide"
            >
              {t("nav.guide")}
            </button>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, delay: 1.25 }}
          className="mt-16 flex flex-wrap gap-x-12 gap-y-6 border-t border-white/10 pt-8"
          data-testid="hero-stats"
        >
          {[
            [String(ALL_COUNTRIES.length), t("hero.statCountries")],
            [`${ALL_CITIES.length}+`, t("hero.statCities")],
            ["24/7", t("hero.statSupport")],
            ["5", t("hero.statLangs")],
          ].map(([num, label], i) => (
            <div key={i}>
              <div className="font-serif text-3xl font-light text-[#e8cf7a]">{num}</div>
              <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-white/45">{label}</div>
            </div>
          ))}
        </motion.div>
      </motion.div>

      <div className="absolute bottom-8 right-8 hidden font-mono text-[10px] tracking-[0.3em] text-white/30 lg:block">
        48.8566° N — 2.3522° E
      </div>
    </section>
  );
}
